/**
 * Parses a scanned QR string as a RealGuest event check-in URL and extracts the
 * `eventId` + `pin` needed to enter the event.
 *
 * The admin check-in page (client.realguest -> events/checkin) encodes:
 *   {checkInUrl}/checkin/{eventId}#pin={pin}   (newer links — PIN in the fragment)
 *   {checkInUrl}/checkin/{eventId}?pin={pin}   (older links / already-printed QRs)
 * e.g. https://checkin.realguest.it/checkin/01ABC...#pin=12345
 *
 * The PIN moved into the fragment so it never reaches server access logs, Referer
 * headers, or link-preview bots; we read the fragment first and fall back to the
 * query, so a QR/link generated by ANY admin version still opens in the app.
 *
 * Host-agnostic on purpose: production is checkin.realguest.it, but dev/staging
 * use other hosts (https://localhost:4201, a staging domain). We validate the
 * CONTRACT (a `/checkin/{eventId}` path + a numeric `pin`) rather than the host,
 * so a QR generated by any RealGuest admin environment opens in the app. Returns
 * null for anything that isn't a well-formed RealGuest check-in URL, so the
 * scanner can reject non-RealGuest codes instead of navigating blindly.
 *
 * The `pin` the app accepts is a numeric code (LoadEventComponent's pinControl is
 * 5 digits); we only sanity-check that it is all digits here and let the server
 * be the final authority on validity.
 */
export interface CheckinTarget {
  eventId: string;
  pin: string;
}

export function parseCheckinUrl(raw: string | null | undefined): CheckinTarget | null {
  if (!raw) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }

  // Only http(s) — reject javascript:, data:, custom schemes, etc.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return null;
  }

  // Path must be exactly /checkin/{eventId} (an optional trailing slash allowed).
  const match = url.pathname.match(/^\/checkin\/([^/]+)\/?$/);
  if (!match) {
    return null;
  }

  const eventId = decodeURIComponent(match[1]).trim();
  // Fragment first (#pin=), query as the legacy fallback (?pin=). URL.hash keeps
  // the leading '#', so strip it before parsing.
  const fragmentParams = new URLSearchParams(url.hash.replace(/^#/, ''));
  const pin = (fragmentParams.get('pin') ?? url.searchParams.get('pin') ?? '').trim();

  if (!eventId || !/^\d+$/.test(pin)) {
    return null;
  }

  return { eventId, pin };
}
